import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useYear } from "../../../context/YearContext";
import { TournamentLink } from "../Tournament/TournamentLink";
import StatusMessage from "../../../components/StatusMessage/StatusMessage.js";
import { MOCK_SCORE_ARRAYS } from "../Individual/mockScores";

// 전광판 본선 화면: 경기 단계별 진출자·결과 (예선은 Top5 의 점수 순 표 그대로)
// round: 대진 라운드, next: 진출자를 가릴 다음 라운드, slots: 칸 수,
// split: 칸을 반씩 두 표로, places: 순위 고정 (승자 먼저, 결승 1·2위 / 3·4위전 3·4위)
const STAGES = {
  individual_round16: { round: "round16", next: "round8", label: "8강 진출자", slots: 8, split: true },
  individual_round8: { round: "round8", next: "round4", label: "4강 진출자", slots: 4 },
  individual_round4: { round: "round4", label: "4강", slots: 4 },
  individual_round3: { round: "round3", label: "3·4위전", slots: 2, places: [3, 4] },
  individual_round2: { round: "round2", label: "결승", slots: 2, places: [1, 2] },
};

// 총점 → 비교전(슛오프) 점수 순, 높은 선수가 앞 (대진표와 같은 기준)
const compareScore = (a, b) =>
  b.total_score - a.total_score || b.special_point - a.special_point;

// 경기 = 조 안에서 등록 순서대로 2명씩 (145m는 한 조에 여러 경기)
const matchesOf = (round) =>
  round.flatMap((jo) => {
    const players = jo?.players || [];
    const pairs = [];
    for (let i = 0; i < players.length; i += 2) pairs.push(players.slice(i, i + 2));
    return pairs;
  });

// 진출자: 다음 라운드에 올라간 선수. 다음 라운드가 아직 없으면 경기마다 점수가 높은 선수 (동점·미입력은 빼둠)
const advancedPlayers = (round, nextRound) => {
  const nextIds = new Set(
    nextRound.flatMap((jo) => (jo?.players || []).map((p) => p.player_id))
  );
  if (nextIds.size > 0) {
    return round
      .flatMap((jo) => jo?.players || [])
      .filter((p) => nextIds.has(p.player_id));
  }
  return matchesOf(round)
    .filter(([a, b]) => a && b && compareScore(a, b) !== 0)
    .map((pair) => [...pair].sort(compareScore)[0]);
};

// 총점 순 등수, 동점은 같은 등수 (1, 2, 3, 3, 3, 6 …)
const rankByTotal = (players) => {
  const sorted = [...players].sort((a, b) => b.total_score - a.total_score);
  return sorted.map((p) => ({
    ...p,
    rank: sorted.findIndex((q) => q.total_score === p.total_score) + 1,
  }));
};

// 결승·3/4위전: 승자가 정해졌을 때만 순위 표시
const rankByPlace = (players, places) => {
  const sorted = [...players].sort(compareScore);
  const decided = sorted.length === 2 && compareScore(sorted[0], sorted[1]) !== 0;
  return sorted.map((p, i) => ({ ...p, rank: decided ? places[i] : "" }));
};

// 테스트(/test): 가상 선수 상위 16명으로 16강 대진(예선 순위 자리)을 짜고 경기마다 점수 높은 선수가 올라감
const SEED_16 = [1, 16, 8, 9, 5, 12, 4, 13, 3, 14, 6, 11, 7, 10, 2, 15];
const mockTournament = (gender) => {
  const players = MOCK_SCORE_ARRAYS[gender].slice(0, 16).map((m) => ({
    player_id: m.player_number,
    player_name: m.player_name,
    country: m.country,
    total_score: m.score_concat.split(",").reduce((sum, s) => sum + Number(s), 0),
    special_point: 0,
  }));
  const toRound = (list) =>
    Array.from({ length: list.length / 2 }, (_, i) => ({
      players: list.slice(i * 2, i * 2 + 2),
    }));
  const play = (list, pickWinner) =>
    toRound(list).map(({ players: [a, b] }) =>
      (compareScore(a, b) <= 0) === pickWinner ? a : b
    );
  const round16 = SEED_16.map((seed) => players[seed - 1]);
  const round8 = play(round16, true);
  const round4 = play(round8, true);
  return {
    round16: toRound(round16),
    round8: toRound(round8),
    round4: toRound(round4),
    round3: toRound(play(round4, false)),
    round2: toRound(play(round4, true)),
  };
};

const TournamentTop = ({ round, gender, distance, subTitle, useMock = false }) => {
  const { t } = useTranslation();
  const { year } = useYear();
  const [tournament, setTournament] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setTournament(null);
    setError(null);
    if (useMock) {
      setTournament(mockTournament(gender));
      return;
    }
    let cancelled = false;
    new TournamentLink()
      .request(gender, distance, year)
      .then((link) => !cancelled && setTournament(link.tournament))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [gender, distance, year, useMock]);

  const stage = STAGES[round];
  if (!stage) return null;

  let content;
  if (error) {
    content = <StatusMessage type="error" description={error} />;
  } else if (!tournament) {
    content = <StatusMessage type="loading" />;
  } else {
    const roundData = tournament[stage.round] || [];
    const players = stage.next
      ? advancedPlayers(roundData, tournament[stage.next] || [])
      : roundData.flatMap((jo) => jo?.players || []);
    const ranked = stage.places
      ? rankByPlace(players, stage.places)
      : rankByTotal(players);
    // 정해진 선수가 칸보다 적으면 빈 줄로 채움 (표 크기 유지)
    const rows = Array.from({ length: stage.slots }, (_, i) => ranked[i] || null);
    const tables = stage.split
      ? [rows.slice(0, stage.slots / 2), rows.slice(stage.slots / 2)]
      : [rows];

    content =
      players.length === 0 ? (
        <StatusMessage />
      ) : (
        <div className={stage.split ? "top5Split" : ""}>
          {tables.map((tableRows, k) => (
            <table key={k}>
              <colgroup>
                <col width="10%" />
                <col width="16%" />
                <col />
                <col width="11%" />
              </colgroup>
              <thead>
                <tr>
                  <th>{t("table.rank")}</th>
                  <th>{t("table.country")}</th>
                  <th>{t("table.player-name")}</th>
                  <th>{t("table.total")}</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((p, i) => (
                  <tr key={i}>
                    <td>{p?.rank ?? ""}</td>
                    <td>{p?.country ?? ""}</td>
                    <td>{p?.player_name ?? ""}</td>
                    <td>{p ? p.total_score : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        </div>
      );
  }

  return (
    <div className="table-box box-1 margin-table">
      <div className="table-wrap result-table">
        <div className="table-shadow">
          <div className="table-title-big">
            <span className="top5Stage">{stage.label}</span>- {distance}m{" "}
            {subTitle} ({gender.toUpperCase()}) -
          </div>
          {content}
        </div>
      </div>
    </div>
  );
};

export default TournamentTop;
